<template>
  <AppWrapper>
    <div class="terms-container">
      <div class="header">
        <span class="grid-text-large">Terms of Service</span>
      </div>
      <div class="content">
        <div class="last-updated">
          <span class="caption">Last updated: November 19, 2024</span>
        </div>

        <section class="terms-section">
          <h2 class="h2">Agreement to Terms</h2>
          <p class="body-text">
            Welcome to Gift a Habit. By accessing or using our mobile
            application ("App") and website, you agree to be bound by these
            Terms of Service. If you disagree with any part of these terms, you
            may not access our services.
          </p>
        </section>

        <section class="terms-section">
          <h2 class="h2">Use of the Service</h2>
          <p class="body-text">
            Gift a Habit is a habit tracking application that allows users to:
          </p>
          <ul class="terms-list">
            <li>Track daily habits</li>
            <li>Set and maintain streaks</li>
            <li>Receive predetermined rewards at milestone achievements</li>
            <li>Gift habits and rewards to other users</li>
          </ul>
          <p class="body-text">
            The service is currently available only on iOS devices and requires
            a valid Apple ID for iCloud synchronization.
          </p>
        </section>

        <section class="terms-section">
          <h2 class="h2">User Responsibilities</h2>
          <p class="body-text">When using Gift a Habit, you agree to:</p>
          <ul class="terms-list">
            <li>Use the service only for lawful purposes</li>
            <li>Not attempt to circumvent any technical limitations</li>
            <li>Not use the service to harass, abuse, or harm others</li>
            <li>Not interfere with or disrupt the service</li>
            <li>
              Take responsibility for all content shared through reward messages
            </li>
          </ul>
        </section>

        <section class="terms-section">
          <h2 class="h2">Free Service and Future Changes</h2>
          <p class="body-text">
            Gift a Habit is currently provided as a free service. While we
            intend to keep the core app free, we reserve the right to:
          </p>
          <ul class="terms-list">
            <li>Modify or discontinue any feature of the service</li>
            <li>Introduce new features or functionality</li>
            <li>Change the terms of service with reasonable notice</li>
          </ul>
        </section>

        <section class="terms-section">
          <h2 class="h2">Software License and Usage</h2>
          <p class="body-text">
            While using our App and website, you are granted a limited,
            non-exclusive, non-transferable, revocable license to use the
            software in accordance with these terms. You may not:
          </p>
          <ul class="terms-list">
            <li>Copy, modify, or create derivative works of the App</li>
            <li>Attempt to decompile or reverse engineer the App</li>
            <li>Distribute or transfer the App to others</li>
            <li>Use the App in any way not permitted by these terms</li>
          </ul>
          <p class="body-text">
            The software, including its original content, features, and
            functionality, is protected by copyright and other applicable laws.
            The use of our service does not grant you ownership of any
            intellectual property rights in our service or the content you
            access.
          </p>
        </section>

        <section class="terms-section">
          <h2 class="h2">Data Storage and iCloud</h2>
          <p class="body-text">
            All habit data is stored locally on your device and synced with your
            personal iCloud account. By using the App, you acknowledge that:
          </p>
          <ul class="terms-list">
            <li>
              You are responsible for maintaining the security of your iCloud
              account
            </li>
            <li>
              Data synchronization depends on your iCloud settings and
              connectivity
            </li>
            <li>
              We cannot recover your data if you lose access to your iCloud
              account
            </li>
            <li>You should maintain appropriate backups of your device</li>
          </ul>
        </section>

        <section class="terms-section">
          <h2 class="h2">Limitation of Liability</h2>
          <p class="body-text">
            To the maximum extent permitted by law, Gift a Habit shall not be
            liable for any indirect, incidental, special, consequential, or
            punitive damages resulting from:
          </p>
          <ul class="terms-list">
            <li>Your use or inability to use the service</li>
            <li>Data loss or corruption</li>
            <li>Unauthorized access to your data</li>
            <li>Bugs, viruses, or other technical issues</li>
          </ul>
        </section>

        <section class="terms-section">
          <h2 class="h2">Termination</h2>
          <p class="body-text">
            We reserve the right to terminate or suspend access to our service
            immediately, without prior notice, for any reason including:
          </p>
          <ul class="terms-list">
            <li>Breach of these Terms</li>
            <li>Harmful or illegal activities</li>
            <li>Technical or security issues</li>
            <li>Extended periods of inactivity</li>
          </ul>
        </section>

        <section class="terms-section">
          <h2 class="h2">Changes to Terms</h2>
          <p class="body-text">
            We reserve the right to modify these terms at any time. We will
            notify users of any material changes by posting the new Terms of
            Service on this page and updating the "Last updated" date. Your
            continued use of the service after such modifications constitutes
            your acceptance of the new terms.
          </p>
        </section>

        <div class="copyright">
          <p class="body-text copyright-text">
            © 2024 Florian Vates. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  </AppWrapper>
</template>

<script setup>
import AppWrapper from "@/components/AppWrapper.vue";
</script>

<style scoped>
.terms-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding-bottom: 60px;
}

.header {
  display: flex;
  flex-direction: column;
  justify-content: end;
  padding: 0 20px;
  height: 15vh;
  min-height: 80px;
  width: 100%;
  align-self: center;
}

.grid-text-large {
  margin-bottom: 20px;
}

.content {
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
}

.last-updated {
  margin-bottom: 40px;
}

.terms-section {
  margin-bottom: 40px;
}

.h2 {
  margin: 25px 0 15px;
  color: var(--Foreground---Primary);
}

.body-text {
  color: var(--Foreground---Secondary);
  font-family: Nunito;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 15px;
}

.terms-list {
  list-style-type: none;
  padding-left: 0;
  margin: 15px 0;
}

.terms-list li {
  color: var(--Foreground---Secondary);
  font-family: Nunito;
  font-size: 16px;
  line-height: 1.6;
  padding-left: 20px;
  position: relative;
  margin-bottom: 8px;
}

.terms-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: var(--Brand);
}

.copyright {
  margin-top: 60px;
  padding-top: 20px;
  border-top: 1px solid var(--Border);
  width: 100%;
}

.copyright-text {
  text-align: center;
  color: var(--Foreground---Secondary);
  font-size: 14px;
}

/* Mobile Responsiveness */
@media only screen and (max-width: 680px) {
  .terms-container {
    padding-bottom: 40px;
  }

  .header {
    background-color: var(--Brand);
  }

  .grid-text-large {
    color: #fff;
  }

  .content {
    padding: 15px;
  }

  .terms-section {
    margin-bottom: 30px;
  }

  .body-text,
  .terms-list li {
    font-size: 14px;
  }
}
</style>
